import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import { JSX, useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { BiTrash, BiSearch } from "react-icons/bi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdCategoryStore } from "./store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import Spinner from "@/components/ui/Spinner";
import PopoverMenu from "@/components/ui/Popeover";
import { Edit, Eye, Trash2 } from "lucide-react";
import TextField from "@/components/ui/Textfield";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/Dialog";

/** Names shown before the list is truncated behind "See more". */
const NAME_LIMIT = 4;

/**
 * A comma-separated list that collapses when long.
 *
 * A category can carry a dozen or more ad types; printing them all pushed the
 * row several lines tall and made the table hard to scan. The first few are
 * shown inline, with the rest revealed on demand.
 */
function ExpandableNames({
  values,
}: {
  values: (string | undefined)[];
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const names = values.filter((v): v is string => !!v);

  if (names.length === 0) {
    return <span>-</span>;
  }

  const isTruncated = names.length > NAME_LIMIT;
  const visible = expanded || !isTruncated ? names : names.slice(0, NAME_LIMIT);

  return (
    <span>
      {visible.join(", ")}
      {isTruncated && (
        <>
          {!expanded && ", "}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-pink-500 hover:underline whitespace-nowrap ml-1"
          >
            {expanded ? "See less" : `See more (${names.length - NAME_LIMIT})`}
          </button>
        </>
      )}
    </span>
  );
}

export default function ManageAdCategories(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [categoryToRestore, setCategoryToRestore] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string>("");
  const itemsPerPage = 25;

  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const {
    resetValue,
    getAdCategoryData,
    adCategoryApiData,
    setEditData,
    getAdCategoryDetailData,
    deleteAdCategory,
    getDeletedAdCategory,
    restoreAdCategory,
  } = useAdCategoryStore(
    useShallow((state) => ({
      resetValue: state.resetValue,
      getAdCategoryData: state.getAdCategoryData,
      adCategoryApiData: state.adCategoryApiData,
      setEditData: state.setEditData,
      getAdCategoryDetailData: state.getAdCategoryDetailData,
      deleteAdCategory: state.deleteAdCategory,
      getDeletedAdCategory: state.getDeletedAdCategory,
      restoreAdCategory: state.restoreAdCategory,
    })),
  );

  const setError = useGlobalStore((state) => state.setError);
  const showSuccess = useGlobalStore((state) => state.showSuccess);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const type = searchParams.get("type");
      if (type === "deleted") {
        await getDeletedAdCategory();
      } else {
        const query: Array<{ key: string; value: string }> = [
          { key: "with", value: "publishers,ad_types,parameter_values" },
        ];
        if (sortField) {
          query.push({ key: "sort", value: sortField });
        }
        await getAdCategoryData(query);
      }
    } catch (error) {
      setError(error.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;

    try {
      await deleteAdCategory(categoryToDelete.id);
      await fetchData();
      showSuccess("Ad category archived successfully");
      setCategoryToDelete(null);
    } catch (error) {
      setError(error.message ?? "Failed to delete category");
    }
  };

  const handleRestore = async () => {
    if (!categoryToRestore) return;

    try {
      await restoreAdCategory(categoryToRestore.id);
      await fetchData();
      showSuccess("Ad category restored successfully");
      setCategoryToRestore(null);
    } catch (error) {
      setError(error.message ?? "Failed to restore category");
    }
  };

  const createMenuItems = (adCategoryId: string, categoryName: string) => [
    {
      label: "View Ad Category",
      icon: <Eye className="w-4 h-4" />,
      onClick: () => navigate(`/ad-categories/publishers?id=${adCategoryId}`),
    },
    {
      label: "Edit Ad Category",
      icon: <Edit className="w-4 h-4" />,
      onClick: async () => {
        await getAdCategoryDetailData(adCategoryId);
        const detailData = useAdCategoryStore.getState().adCategoryDetailData;
        if (detailData) {
          setEditData(detailData);
        }
        navigate(`/create-ad-categories?id=${adCategoryId}`);
      },
      showDivider: true,
    },
    {
      label: "Archive Ad Category",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: () =>
        setCategoryToDelete({ id: adCategoryId, name: categoryName }),
    },
  ];

  const deletedMenuItem = (adCategoryId: string, categoryName: string) => [
    {
      label: `Re-activate ${categoryName}`,
      icon: <Trash2 className="w-4 h-4" />,
      onClick: () =>
        setCategoryToRestore({ id: adCategoryId, name: categoryName }),
    },
  ];

  useEffect(() => {
    fetchData();
  }, [sortField, searchParams]);

  // Filter categories based on search
  const filteredCategories = Array.isArray(adCategoryApiData?.data)
    ? adCategoryApiData.data.filter((category) =>
        category.name.toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  // Calculate pagination details
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentCategories = filteredCategories.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const goPrevious = () => {
    setCurrentPage((page) => Math.max(page - 1, 1));
  };

  const goNext = () => {
    setCurrentPage((page) => Math.min(page + 1, totalPages));
  };

  const handleSort = (field: string) => {
    let newSort: string;
    if (sortField === field) {
      newSort = `-${field}`;
    } else if (sortField === `-${field}`) {
      newSort = '';
    } else {
      newSort = field;
    }
    setSortField(newSort);
    setCurrentPage(1);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField === field) {
      return (
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 15l6-6 6 6" />
        </svg>
      );
    }
    if (sortField === `-${field}`) {
      return (
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">
            {searchParams.get("type") === "deleted" ? "Archived" : null} Ad
            Categories Dashboard
          </h1>
          <Button
            type="text"
            className="text-gray-500 hover:text-gray-700 underline text-sm"
            onClick={() => {
              if (searchParams.get("type") === "deleted") {
                searchParams.delete("type");
                setSearchParams(searchParams);
              } else {
                setSearchParams({
                  ...searchParams,
                  type: "deleted",
                });
              }
            }}
          >
            {searchParams.get("type") === "deleted"
              ? "Hide Archived Ad Categories"
              : "View Archived Ad Categories"}
          </Button>
        </div>

        <div className="flex justify-between mb-6">
          <div>
            <TextField
              type="text"
              leadingIcon={<BiSearch />}
              placeholder="Search by Category name..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-96"
            />
          </div>
          <div>
            <div>
              <Button
                trailingIcon={<FaPlus />}
                type="primary"
                onClick={() => {
                  resetValue();
                  navigate("/create-ad-categories");
                }}
              >
                Add New Category
              </Button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="pt-2">
              <table className="w-full mt-5">
                <thead className="border-b border-gray-300 text-gray-600/50 text-sm">
                  {/* Explicit widths: without them the browser sized each
                      column to its content, so "Status" and "Publishers" ran
                      together while Ad Types took the rest of the row. */}
                  <tr>
                    <th className="text-left py-2 pr-4 w-[18%]">
                      <div
                        className="flex items-center cursor-pointer gap-1 select-none"
                        onClick={() => handleSort("name")}
                      >
                        Category Name
                        <SortIcon field="name" />
                      </div>
                    </th>
                    <th className="text-left py-2 pr-4 w-[10%]">
                      <div
                        className="flex items-center cursor-pointer gap-1 select-none"
                        onClick={() => handleSort("status")}
                      >
                        Status
                        <SortIcon field="status" />
                      </div>
                    </th>
                    <th className="text-left py-2 pr-4 w-[22%]">
                      <div
                        className="flex items-center cursor-pointer gap-1 select-none"
                        onClick={() => handleSort("publishers")}
                      >
                        Publishers
                        <SortIcon field="publishers" />
                      </div>
                    </th>
                    <th className="text-left py-2 pr-4">
                      <div
                        className="flex items-center cursor-pointer gap-1 select-none"
                        onClick={() => handleSort("ad_types")}
                      >
                        Ad Types
                        <SortIcon field="ad_types" />
                      </div>
                    </th>
                    <th className="w-12" />
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {currentCategories.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-6 text-center text-gray-500"
                      >
                        No Data Found
                      </td>
                    </tr>
                  ) : (
                    currentCategories.map((adCategory, i) => (
                      // align-top keeps the short cells level with the first
                      // line of a long, wrapped list.
                      <tr
                        key={i}
                        className="border-b border-gray-200 align-top"
                      >
                        <td className="py-4 pr-4 font-semibold">
                          {adCategory.name}
                        </td>
                        <td className="py-4 pr-4">
                          {searchParams.get("type") === "deleted"
                            ? "Archived"
                            : "Active"}
                        </td>
                        <td className="py-4 pr-4">
                          <ExpandableNames
                            values={
                              Array.isArray(adCategory.publishers)
                                ? adCategory.publishers.map((p) => p?.name)
                                : []
                            }
                          />
                        </td>
                        <td className="py-4 pr-4">
                          <ExpandableNames
                            values={
                              Array.isArray(adCategory.ad_types)
                                ? adCategory.ad_types.map((a) => a?.name)
                                : []
                            }
                          />
                        </td>
                        <td>
                          <PopoverMenu
                            items={
                              searchParams.get("type")
                                ? deletedMenuItem(
                                    adCategory.ad_category_id,
                                    adCategory.name,
                                  )
                                : createMenuItems(
                                    adCategory.ad_category_id,
                                    adCategory.name,
                                  )
                            }
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end items-center mt-6 space-x-4 text-sm">
              <Button
                type="secondary"
                onClick={goPrevious}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span>
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button
                type="secondary"
                onClick={goNext}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={!!categoryToDelete}
        onOpenChange={() => setCategoryToDelete(null)}
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <div className="font-semibold text-xl text-center">
              Archive Ad Category
            </div>
            <DialogDescription className="mt-4 text-center text-gray-600">
              Are you sure you want to archive{" "}
              <span className="font-semibold">{categoryToDelete?.name}</span>?
              This action can be undone by re-activating the category.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-4 mt-6">
            <Button type="secondary" onClick={() => setCategoryToDelete(null)}>
              Cancel
            </Button>
            <Button
              type="primary"
              trailingIcon={<BiTrash fontSize={18} />}
              onClick={handleDelete}
            >
              Yes, Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-activate Confirmation Dialog */}
      <Dialog
        open={!!categoryToRestore}
        onOpenChange={() => setCategoryToRestore(null)}
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <div className="font-semibold text-xl text-center">
              Re-activate Ad Category
            </div>
            <DialogDescription className="mt-4 text-center text-gray-600">
              Are you sure you want to re-activate{" "}
              <span className="font-semibold">{categoryToRestore?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-4 mt-6">
            <Button type="secondary" onClick={() => setCategoryToRestore(null)}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleRestore}>
              Yes, Re-activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
